import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../util/auth';
import { useRouter, Link } from '../../util/router';
import { submitAdminApplication } from '../../util/util';
import supabase from '../../util/supabase';
import PageLoader from '../PageLoader';
import AdminApplyStepAccount from './AdminApplyStepAccount';
import AdminApplyStepOrg from './AdminApplyStepOrg';
import AdminApplyStepInfo from './AdminApplyStepInfo';
import AdminApplyStepReview from './AdminApplyStepReview';

const TOTAL_STEPS = 4;
const SIGNIN_PATH = '/auth/signin?next=/admin/apply';

function AdminApplySection() {
  const auth = useAuth();
  const router = useRouter();

  const [step, setStep] = useState(1);
  const [switchingAccount, setSwitchingAccount] = useState(false);
  const [orgData, setOrgData] = useState({
    orgMode: 'existing',
    orgId: null,
    orgName: '',
    orgBio: '',
  });
  const [infoData, setInfoData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (auth.user === null) return;
    if (auth.user === false) {
      router.replace(SIGNIN_PATH);
    }
  }, [auth.user, router]);

  const handleUseDifferentEmail = useCallback(() => {
    setSwitchingAccount(true);
    supabase.auth.signOut().then(() => {
      router.replace(SIGNIN_PATH);
    });
  }, [router]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitAdminApplication(orgData, infoData);
      setSubmitted(true);
    } catch (error) {
      setSubmitError(error.message);
    } finally {
      setSubmitting(false);
    }
  }, [orgData, infoData]);

  if (auth.user === null || auth.user === false || switchingAccount) {
    return <PageLoader />;
  }

  return (
    <section className="min-h-[calc(100vh-80px)] bg-white flex flex-col justify-center px-4 py-8">
      <div className="mx-auto w-full max-w-lg">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-semibold tracking-wide uppercase text-secondary-red">
            Request Access
          </h1>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-secondary-red">
          {submitted ? (
            <div className="py-4 text-center space-y-4">
              <p className="text-lg font-medium text-slate-700">Request received!</p>
              <p className="text-sm text-slate-400">
                Our team will review your submission and reach out to you shortly.
              </p>
              <p className="text-sm text-slate-400">
                In the meantime, you can also email us at{' '}
                <a href="mailto:info@showrunner.co" className="text-secondary-red hover:underline">
                  info@showrunner.co
                </a>
              </p>
              <Link to="/admin/dashboard" className="inline-block mt-2 text-sm text-secondary-red hover:underline">
                Go to dashboard
              </Link>
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-400 mb-4">
                Step {step} of {TOTAL_STEPS}
              </p>

              {step === 1 && (
                <AdminApplyStepAccount
                  user={auth.user}
                  onContinue={() => setStep(2)}
                  onUseDifferentEmail={handleUseDifferentEmail}
                />
              )}

              {step === 2 && (
                <AdminApplyStepOrg orgData={orgData} setOrgData={setOrgData} onNext={() => setStep(3)} />
              )}

              {step === 3 && (
                <AdminApplyStepInfo
                  initialData={infoData}
                  onBack={() => setStep(2)}
                  onNext={data => {
                    setInfoData(data);
                    setStep(4);
                  }}
                />
              )}

              {step === 4 && (
                <AdminApplyStepReview
                  orgData={orgData}
                  infoData={infoData}
                  submitting={submitting}
                  submitError={submitError}
                  onBack={() => setStep(3)}
                  onSubmit={handleSubmit}
                />
              )}
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          This section is for organization admins.
        </p>
      </div>
    </section>
  );
}

export default AdminApplySection;
